import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { AdminNavCounts } from '@bravemove/types';
import { adminNavCountsApi } from '../api/navCounts';
import { onAdminMutation } from '../auth/adminMutations';
import { usePollWhileVisible, type PollTrigger } from '@bravemove/ui-web';
import { NavCountsContext } from './NavCountsContext';

/** Poll once a minute while visible; every 5 min after 10 min without input. */
const POLL = { intervalMs: 60_000, idleAfterMs: 10 * 60_000, idleIntervalMs: 5 * 60_000 };

/** Coalesces a burst of admin actions (bulk approve) into one refresh. */
const MUTATION_DEBOUNCE_MS = 500;

/**
 * F60b — the one source of the admin console's badge counts
 * (plans/F60b_badges_and_health_ui.md). Replaces 18 per-badge 60 s polls with
 * one `GET /admin/nav-counts` per visible tab, and refreshes right after any
 * admin action (`adminFetch` signals every successful admin mutation).
 * Mounted once in `AdminShell`; the sidebar and the dashboard read it through
 * `useNavCounts` / `useNavCount`.
 */
export function NavCountsProvider({ children }: { readonly children: ReactNode }) {
  const [counts, setCounts] = useState<AdminNavCounts>({});

  const load = useCallback(async (trigger: PollTrigger) => {
    const next = await adminNavCountsApi.get({ fresh: trigger === 'request' });
    // Merge: a count the API couldn't compute this time keeps its last value.
    setCounts((prev) => ({ ...prev, ...next }));
  }, []);

  const { refreshNow } = usePollWhileVisible(load, POLL);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const unsubscribe = onAdminMutation(() => {
      if (timer !== null) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        refreshNow();
      }, MUTATION_DEBOUNCE_MS);
    });
    return () => {
      unsubscribe();
      if (timer !== null) clearTimeout(timer);
    };
  }, [refreshNow]);

  const value = useMemo(() => ({ counts, refresh: refreshNow }), [counts, refreshNow]);
  return <NavCountsContext.Provider value={value}>{children}</NavCountsContext.Provider>;
}
